"use client";
import { motion } from "framer-motion";
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaPinterest,
  FaYoutube,
  FaLinkedinIn,
} from "react-icons/fa";

interface FooterLink {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
  social?: string;
}

interface CategoryLink {
  id: string;
  name: string;
  slug: string;
}

interface Address {
  id: number;
  text: string;
  address: string;
  country: string;
  phone?: string;
  email?: string;
}

function RenderSocialIcon({ social }: { social: string | undefined }) {
  const iconClass = "text-lg hover:text-blue-400 transition-colors duration-300";

  switch (social) {
    case "TWITTER":
      return <FaTwitter className={iconClass} />;
    case "INSTAGRAM":
      return <FaInstagram className={iconClass} />;
    case "PINTEREST":
      return <FaPinterest className={iconClass} />;
    case "FACEBOOK":
      return <FaFacebookF className={iconClass} />;
    case "YOUTUBE":
      return <FaYoutube className={iconClass} />;
    case "LINKEDIN":
      return <FaLinkedinIn className={iconClass} />;
    default:
      return null;
  }
}

export default function Footer_Layout2({
  logoUrl,
  logoText,
  menuLinks,
  legalLinks,
  socialLinks,
  addresses,
  images,
}: {
  logoUrl: string | null;
  logoText: string | null;
  menuLinks: Array<FooterLink>;
  legalLinks: Array<FooterLink>;
  socialLinks: Array<FooterLink>;
  addresses: Array<Address>;
  images?: any;
}) {
  const bgUrl = images && Array.isArray(images) && images.length > 0 ? images[0]?.url : null;

  return (
    <footer className="relative bg-gray-50 text-gray-800 overflow-hidden">
      <div className="container mx-auto px-5 py-12 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8">
          {/* Brand section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "0px" }}
            className="flex flex-col items-center md:items-start"
          >
            {logoUrl && (
              <div className="mb-4">
                <img
                  src={logoUrl}
                  alt={logoText || "Logo"}
                  className="h-10 object-contain"
                />
              </div>
            )}
            {logoText && (
              <p className="text-sm text-gray-600 mb-4 text-center md:text-left max-w-xs">
                {logoText}
              </p>
            )}
          </motion.div>

          {/* Links section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true, margin: "0px" }}
            className="flex flex-wrap justify-center gap-6"
          >
            {menuLinks.map((link, index) => (
              <motion.a
                key={link.id}
                href={link.url}
                target={link.newTab ? "_blank" : "_self"}
                whileHover={{ y: -2 }}
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {link.text}
              </motion.a>
            ))}
          </motion.div>

          {/* Social links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            viewport={{ once: true, margin: "0px" }}
            className="flex gap-4"
          >
            {socialLinks.map((link) => (
              <motion.a
                key={link.id}
                href={link.url}
                target={link.newTab ? "_blank" : "_self"}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.95 }}
                className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-colors duration-300 shadow-sm"
                aria-label={`Follow us on ${link.social}`}
              >
                <RenderSocialIcon social={link.social} />
              </motion.a>
            ))}
          </motion.div>
        </div>

        <div className="border-t border-gray-200 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            viewport={{ once: true, margin: "0px" }}
            className="text-xs text-gray-500"
          >
            © {new Date().getFullYear()} {logoText || "Company"}. All rights reserved.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            viewport={{ once: true, margin: "0px" }}
            className="flex flex-wrap justify-center gap-4"
          >
            {legalLinks.map((link, index) => (
              <motion.a
                key={index}
                href={link.url}
                target={link.newTab ? "_blank" : "_self"}
                whileHover={{ color: "#4f46e5" }}
                className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                {link.text}
              </motion.a>
            ))}
          </motion.div>
        </div>
      </div>
    </footer>
  );
}